<template>
	<div class="status-badge-dropdown" ref="dropdownRef">
		<div 
			class="status-badge-trigger" 
			:class="[`status-${currentStatus}`, { 'is-disabled': disabled }]"
			@click="toggleDropdown"
		>
			<span class="status-text">{{ statusLabel }}</span>
			<BsChevronDown 
				class="chevron-icon" 
				:class="{ 'is-open': isOpen }"
			/>
		</div>
		
		<transition name="dropdown-fade">
			<div v-if="isOpen" class="status-dropdown-menu">
				<div 
					v-for="status in statusOptions" 
					:key="status.value"
					class="status-option"
					:class="{ 'is-selected': status.value === currentStatus }"
					@click="selectStatus(status.value)"
				>
					{{ status.label }}
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { BsChevronDown } from '@kalimahapps/vue-icons';

const props = defineProps({
	currentStatus: {
		type: String,
		default: 'unpaid',
		validator: (value) => ['unpaid', 'paid'].includes(value)
	},
	disabled: {
		type: Boolean,
		default: false,
	}
});

const emit = defineEmits(['update:status']);

const isOpen = ref(false);
const dropdownRef = ref(null);

const statusOptions = [
	{ value: 'unpaid', label: 'Unpaid' },
	{ value: 'paid', label: 'Paid' }
];

const statusLabel = computed(() => {
	const option = statusOptions.find(opt => opt.value === props.currentStatus);
	return option ? option.label : props.currentStatus.charAt(0).toUpperCase() + props.currentStatus.slice(1);
});

const toggleDropdown = () => {
	if (props.disabled) return;
	isOpen.value = !isOpen.value;
};

const selectStatus = (status) => {
	if (props.disabled) return;
	if (status !== props.currentStatus) {
		emit('update:status', status);
	}
	isOpen.value = false;
};

const handleClickOutside = (event) => {
	if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
		isOpen.value = false;
	}
};

onMounted(() => {
	document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
	document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="sass" scoped>
@use '@/assets/styles/variables.sass' as variables

.status-badge-dropdown
	position: relative
	display: inline-block

.status-badge-trigger
	display: inline-flex
	align-items: center
	gap: variables.$spacing-xsmall
	padding: variables.$spacing-small variables.$spacing-large
	border-radius: variables.$border-radius
	font-size: variables.$font-size-small
	font-weight: 500
	text-transform: capitalize
	cursor: pointer
	transition: all 0.2s ease
	user-select: none
	font-family: inherit
	height: 42px
	border: 1px solid transparent
	box-shadow: var(--box-shadow-button)
	
	&:hover
		opacity: 0.85

	&.is-disabled
		opacity: 0.6
		cursor: not-allowed
		pointer-events: none
	
	&.status-unpaid
		background-color: var(--noc-blue-10)
		color: var(--noc-blue)
		
	&.status-paid
		background-color: var(--noc-blue-10)
		color: var(--noc-blue)

.status-text
	font-size: variables.$font-size-small

.chevron-icon
	font-size: 0.9rem
	transition: transform 0.2s ease
	
	&.is-open
		transform: rotate(180deg)

.status-dropdown-menu
	position: absolute
	top: calc(100% + variables.$spacing-xsmall)
	right: 0
	min-width: 140px
	background-color: var(--noc-background)
	border: 1px solid var(--border-color)
	border-radius: variables.$border-radius
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)
	overflow: hidden
	z-index: 1000

.status-option
	padding: variables.$spacing-small variables.$spacing-medium
	cursor: pointer
	transition: background-color 0.1s ease
	font-size: 0.875rem
	color: var(--text-color)
	
	&:hover
		background-color: var(--noc-background-secondary)
		
	&.is-selected
		background-color: var(--noc-background-subtle)
		font-weight: 500

// Dropdown animation
.dropdown-fade-enter-active,
.dropdown-fade-leave-active
	transition: opacity 0.2s ease, transform 0.2s ease

.dropdown-fade-enter-from,
.dropdown-fade-leave-to
	opacity: 0
	transform: translateY(-4px)
</style>
